"use client";

import { usePathname } from "next/navigation";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  type DataLayerUser,
  getDataLayerUser,
} from "@/lib/server/actions/get-datalayer-user";
import { userToDataLayer } from "@/utils/gtm.utils";

const DataLayerUserContext = createContext<DataLayerUser | null>(null);

/**
 * The current analytics user. `PageAnalytics` reads it so `dataLayer_ready`
 * carries `user` synchronously on page load.
 */
export function useDataLayerUser(): DataLayerUser | null {
  return useContext(DataLayerUserContext);
}

function sameUser(a: DataLayerUser, b: DataLayerUser): boolean {
  return (
    a.id === b.id &&
    a.login_method === b.login_method &&
    a.login_status === b.login_status
  );
}

interface DataLayerIdentityProviderProps {
  initialUser: DataLayerUser;
  children: ReactNode;
}

/**
 * Owns the analytics identity for the whole localized app. Mounted once in the
 * layout, so it keeps the dataLayer user current on every route - including ones
 * that never mount `PageAnalytics` - and across login/logout without a refresh.
 *
 * The initial user is server-resolved and handed to `PageAnalytics` via context,
 * so `dataLayer_ready` fires with it synchronously (no async ordering race). This
 * provider only pushes a `user_data_update` when the identity actually changes:
 * after a client-side login redirect, or on logout.
 */
export function DataLayerIdentityProvider({
  initialUser,
  children,
}: DataLayerIdentityProviderProps) {
  const [user, setUser] = useState<DataLayerUser>(initialUser);
  // What the dataLayer already reflects. The initial user is emitted inside
  // dataLayer_ready by PageAnalytics, so it counts as already pushed - we only
  // emit user_data_update for later changes.
  const lastPushedRef = useRef<DataLayerUser>(initialUser);
  // Refreshes can overlap (navigation vs. logout). Only the latest request may
  // apply its result, otherwise an older authenticated response resolving after
  // the logout one would restore the previous customer.
  const requestIdRef = useRef(0);
  const pathname = usePathname();

  const refresh = useCallback(async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const next = await getDataLayerUser();
    if (requestId !== requestIdRef.current) {
      return;
    }
    if (sameUser(next, lastPushedRef.current)) {
      return;
    }
    lastPushedRef.current = next;
    setUser(next);
    userToDataLayer(next);
  }, []);

  // Runs on load and after every client navigation (password/register logins
  // redirect via the router, which keeps this provider mounted) so the identity
  // is resolved and kept current everywhere. `pathname` is a dependency on
  // purpose: it is the signal that a navigation happened, even though the effect
  // body does not read it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-resolve identity on every navigation
  useEffect(() => {
    refresh().catch(() => {
      // Best-effort: keep the current identity on failure.
    });
  }, [pathname, refresh]);

  // Logout dispatches "user-logout" before its delayed redirect; clear the
  // identity immediately so tracking never reports the previous customer.
  useEffect(() => {
    const handleLogout = () => {
      refresh().catch(() => {
        // Best-effort.
      });
    };
    document.addEventListener("user-logout", handleLogout);
    return () => document.removeEventListener("user-logout", handleLogout);
  }, [refresh]);

  return (
    <DataLayerUserContext.Provider value={user}>
      {children}
    </DataLayerUserContext.Provider>
  );
}
